/**
 * Telehealth TypeScript Types and Interfaces
 *
 * Defines types for telehealth sessions, encounters, and related components
 */

// ============================================================================
// ENUMS / UNION TYPES
// ============================================================================

export type SessionStatus =
  | 'scheduled'
  | 'waiting'
  | 'in_progress'
  | 'completed'
  | 'no_show'
  | 'cancelled'
  | 'technical_failure';

export type SessionOutcome =
  | 'completed_full'
  | 'completed_partial'
  | 'rescheduled'
  | 'no_show_member'
  | 'no_show_coordinator'
  | 'technical_failure';

export type EncounterStatus =
  | 'draft'
  | 'in_progress'
  | 'completed'
  | 'signed'
  | 'amended'
  | 'locked';

export type EncounterType =
  | 'telehealth_visit'
  | 'care_coordination'
  | 'assessment'
  | 'follow_up'
  | 'crisis_intervention';

export type ConnectionQuality = 'excellent' | 'good' | 'fair' | 'poor';

export type MeetingProvider =
  | 'twilio'
  | 'jitsi'
  | 'zoom'
  | 'google_meet'
  | 'teams'
  | 'webex'
  | 'phone';

export type AssessmentCompletionStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'skipped';

// SDOH Domain codes
export type SDOHDomainCode =
  | 'HOUSING'
  | 'FOOD_NUTRITION'
  | 'TRANSPORTATION'
  | 'ECONOMIC_STABILITY'
  | 'EDUCATION_EMPLOYMENT'
  | 'SOCIAL_SUPPORT'
  | 'HEALTH_BEHAVIORS'
  | 'SAFETY';

// ============================================================================
// CORE INTERFACES
// ============================================================================

export interface TelehealthSession {
  id: number;
  cbo_id: number;
  calendar_event_id: number;
  member_id: number;
  care_coordinator_id: number;
  status: SessionStatus;
  scheduled_start_time: string;
  scheduled_duration_minutes: number;
  actual_start_time?: string;
  actual_end_time?: string;
  actual_duration_minutes?: number;
  member_joined_waiting_at?: string;
  coordinator_joined_at?: string;
  session_started_at?: string;
  meeting_provider: MeetingProvider | string;
  meeting_link?: string;
  meeting_room_id?: string;
  is_audio_only: boolean;
  recording_enabled: boolean;
  recording_consent_obtained: boolean;
  recording_url?: string;
  connection_quality?: ConnectionQuality;
  technical_issues_reported?: string;
  session_outcome?: SessionOutcome;
  outcome_notes?: string;
  follow_up_required: boolean;
  follow_up_scheduled_date?: string;
  created_at: string;
  updated_at: string;

  // Joined fields from view
  member_name?: string;
  member_phone?: string;
  member_email?: string;
  coordinator_name?: string;
  coordinator_email?: string;
  event_title?: string;
  event_description?: string;
  encounter_id?: number;
  encounter_status?: EncounterStatus;
}

export interface TelehealthEncounter {
  id: number;
  cbo_id: number;
  telehealth_session_id: number;
  member_id: number;
  documenting_user_id: number;
  encounter_date: string;
  encounter_start_time?: string;
  encounter_end_time?: string;
  encounter_type: EncounterType | string;
  place_of_service_code: string;
  billing_activity_id?: number;
  chief_complaint?: string;
  reason_for_visit?: string;
  subjective_notes?: string;
  objective_notes?: string;
  assessment_notes?: string;
  plan_notes?: string;
  member_goals_discussed?: string;
  barriers_identified?: string;
  interventions_provided?: string;
  resources_provided?: string;
  sdoh_domains_addressed: SDOHDomainCode[];
  referrals_generated: number;
  verbal_consent_obtained: boolean;
  consent_notes?: string;
  status: EncounterStatus;
  drafted_at: string;
  completed_at?: string;
  signed_at?: string;
  signed_by?: number;
  locked_at?: string;
  created_at: string;
  updated_at: string;

  // Joined fields from view
  member_name?: string;
  documenter_name?: string;
  session_status?: SessionStatus;
  session_duration?: number;
  assessments_count?: number;
  case_notes_count?: number;
}

// ============================================================================
// REQUEST/CREATE INTERFACES
// ============================================================================

export interface TelehealthSessionCreate {
  calendar_event_id: number;
  member_id: number;
  scheduled_duration_minutes?: number;
  is_audio_only?: boolean;
  recording_enabled?: boolean;
}

export interface TelehealthSessionUpdate {
  status?: SessionStatus;
  session_outcome?: SessionOutcome;
  outcome_notes?: string;
  technical_issues_reported?: string;
  connection_quality?: ConnectionQuality;
  follow_up_required?: boolean;
  follow_up_scheduled_date?: string;
  recording_consent_obtained?: boolean;
}

export interface EndSessionRequest {
  outcome: SessionOutcome;
  outcome_notes?: string;
  connection_quality?: ConnectionQuality;
  technical_issues_reported?: string;
}

export interface EncounterDocumentation {
  chief_complaint?: string;
  reason_for_visit?: string;
  subjective_notes?: string;
  objective_notes?: string;
  assessment_notes?: string;
  plan_notes?: string;
  member_goals_discussed?: string;
  barriers_identified?: string;
  interventions_provided?: string;
  resources_provided?: string;
  sdoh_domains_addressed?: SDOHDomainCode[];
}

export interface TelehealthEncounterCreate {
  telehealth_session_id: number;
  encounter_type?: EncounterType;
  place_of_service_code?: string;
  billing_activity_id?: number;
  documentation?: EncounterDocumentation;
}

export interface TelehealthEncounterUpdate {
  documentation?: EncounterDocumentation;
  status?: EncounterStatus;
  encounter_type?: EncounterType | string;
  form_instance_id?: number;
  verbal_consent_obtained?: boolean;
  consent_notes?: string;
}

export interface LinkAssessmentRequest {
  sdoh_assessment_id: number;
  assessor_notes?: string;
}

export interface LinkCaseNoteRequest {
  case_note_id: number;
  created_during_session?: boolean;
}

// ============================================================================
// RESPONSE INTERFACES
// ============================================================================

export interface TelehealthSessionListResponse {
  sessions: TelehealthSession[];
  total: number;
  page: number;
  page_size: number;
}

export interface TelehealthEncounterListResponse {
  encounters: TelehealthEncounter[];
  total: number;
  page: number;
  page_size: number;
}

export interface StartSessionResponse {
  session_id: number;
  status: string;
  started_at: string;
  encounter_id: number;
  meeting_link: string;
  meeting_room_id?: string;
}

export interface EndSessionResponse {
  session_id: number;
  status: string;
  ended_at: string;
  actual_duration_minutes: number;
  encounter_id: number;
}

export interface SessionAssessmentResponse {
  id: number;
  telehealth_session_id: number;
  encounter_id?: number;
  sdoh_assessment_id: number;
  completion_status: AssessmentCompletionStatus;
  started_at?: string;
  completed_at?: string;
  assessor_notes?: string;
  created_at: string;
}

export interface SessionStateHistoryResponse {
  id: number;
  telehealth_session_id: number;
  from_status?: SessionStatus;
  to_status: SessionStatus;
  changed_at: string;
  changed_by?: number;
  change_reason?: string;
  metadata: Record<string, unknown>;
}

// ============================================================================
// REPORTING INTERFACES
// ============================================================================

export interface SessionMetrics {
  total_scheduled: number;
  total_completed: number;
  total_no_shows: number;
  total_cancelled: number;
  total_technical_failures: number;
  completion_rate: number;
  no_show_rate: number;
  avg_duration_minutes: number;
}

export interface EncounterMetrics {
  total_documented: number;
  total_signed: number;
  total_draft: number;
  avg_time_to_sign_hours: number;
  documentation_completion_rate: number;
}

export interface SDOHDomainMetric {
  domain: SDOHDomainCode;
  domain_label: string;
  count: number;
  percentage: number;
}

export interface CoordinatorMetric {
  user_id: number;
  user_name: string;
  sessions_completed: number;
  avg_duration_minutes: number;
  completion_rate: number;
}

export interface TelehealthReportSummary {
  period_start: string;
  period_end: string;
  sessions: SessionMetrics;
  encounters: EncounterMetrics;
  sdoh_domains: SDOHDomainMetric[];
  by_coordinator: CoordinatorMetric[];
}

// ============================================================================
// COMPONENT PROPS INTERFACES
// ============================================================================

export interface TelehealthSessionListProps {
  memberId?: number;
  coordinatorId?: number;
  status?: SessionStatus;
  dateRange?: { start: Date; end: Date };
  onSessionSelect?: (session: TelehealthSession) => void;
  showCreateButton?: boolean;
  className?: string;
}

export interface TelehealthSessionCardProps {
  session: TelehealthSession;
  onClick?: (session: TelehealthSession) => void;
  onJoin?: (session: TelehealthSession) => void;
  onCancel?: (session: TelehealthSession) => void;
  showActions?: boolean;
  compact?: boolean;
  className?: string;
}

export interface TelehealthWaitingRoomProps {
  sessionId: number;
  onJoinCall: () => void;
  onCancel: () => void;
  onMarkNoShow?: () => void;
  className?: string;
}

export interface TelehealthVideoRoomProps {
  session: TelehealthSession;
  encounter: TelehealthEncounter;
  onEndCall: (outcome: SessionOutcome, notes?: string) => void;
  onDocumentationUpdate: (updates: Partial<EncounterDocumentation>) => void;
  className?: string;
}

export interface EncounterDocumentationPanelProps {
  encounter: TelehealthEncounter;
  isEditing: boolean;
  onUpdate: (updates: Partial<EncounterDocumentation>) => void;
  onSave: () => void;
  onSign?: () => void;
  autoSaveEnabled?: boolean;
  autoSaveIntervalMs?: number;
  className?: string;
}

export interface SessionStatusBadgeProps {
  status: SessionStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export interface ScheduleTelehealthModalProps {
  open: boolean;
  onClose: () => void;
  memberId: number;
  memberName: string;
  defaultDate?: Date;
  onScheduled?: (session: TelehealthSession) => void;
}

export interface TelehealthDashboardProps {
  className?: string;
  onNavigate?: (tab: string, params?: { sessionId?: number }) => void;
}

export interface TelehealthReportingProps {
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  scheduled: 'Scheduled',
  waiting: 'Waiting',
  in_progress: 'In Progress',
  completed: 'Completed',
  no_show: 'No Show',
  cancelled: 'Cancelled',
  technical_failure: 'Technical Failure',
};

export const SESSION_STATUS_COLORS: Record<SessionStatus, string> = {
  scheduled: 'blue',
  waiting: 'yellow',
  in_progress: 'green',
  completed: 'gray',
  no_show: 'red',
  cancelled: 'gray',
  technical_failure: 'orange',
};

export const ENCOUNTER_STATUS_LABELS: Record<EncounterStatus, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  completed: 'Completed',
  signed: 'Signed',
  amended: 'Amended',
  locked: 'Locked',
};

export const SESSION_OUTCOME_LABELS: Record<SessionOutcome, string> = {
  completed_full: 'Completed (Full)',
  completed_partial: 'Completed (Partial)',
  rescheduled: 'Rescheduled',
  no_show_member: 'No Show (Member)',
  no_show_coordinator: 'No Show (Coordinator)',
  technical_failure: 'Technical Failure',
};

export const SDOH_DOMAINS: { code: SDOHDomainCode; label: string; icon: string }[] = [
  { code: 'HOUSING', label: 'Housing Stability', icon: 'Home' },
  { code: 'FOOD_NUTRITION', label: 'Food & Nutrition', icon: 'Utensils' },
  { code: 'TRANSPORTATION', label: 'Transportation', icon: 'Car' },
  { code: 'ECONOMIC_STABILITY', label: 'Economic Stability', icon: 'DollarSign' },
  { code: 'EDUCATION_EMPLOYMENT', label: 'Education & Employment', icon: 'GraduationCap' },
  { code: 'SOCIAL_SUPPORT', label: 'Social Support', icon: 'Users' },
  { code: 'HEALTH_BEHAVIORS', label: 'Health Behaviors', icon: 'Heart' },
  { code: 'SAFETY', label: 'Safety', icon: 'Shield' },
];

export const ENCOUNTER_TYPES: { value: EncounterType; label: string }[] = [
  { value: 'telehealth_visit', label: 'Telehealth Visit' },
  { value: 'care_coordination', label: 'Care Coordination' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'crisis_intervention', label: 'Crisis Intervention' },
];

export const CONNECTION_QUALITY_OPTIONS: { value: ConnectionQuality; label: string; color: string }[] = [
  { value: 'excellent', label: 'Excellent', color: 'green' },
  { value: 'good', label: 'Good', color: 'blue' },
  { value: 'fair', label: 'Fair', color: 'yellow' },
  { value: 'poor', label: 'Poor', color: 'red' },
];

// ============================================================================
// VOICE AI TYPES
// ============================================================================

export type VoiceAIConsentStatus =
  | 'not_requested'
  | 'pending'
  | 'granted'
  | 'denied'
  | 'withdrawn';

export type VoiceAIConsentMethod = 'verbal' | 'written' | 'digital';

export type TranscriptionStatus =
  | 'recording'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type VoiceAIProvider = 'deepgram' | 'assemblyai' | 'aws_transcribe';

export interface VoiceAIConsentRequest {
  consent_granted: boolean;
  consent_method: VoiceAIConsentMethod;
  notes?: string;
}

export interface VoiceAIConsentResponse {
  session_id: number;
  consent_status: VoiceAIConsentStatus;
  consent_timestamp?: string;
  consent_method?: VoiceAIConsentMethod;
  consented_by_member_id?: number;
  voice_ai_enabled?: boolean;
}

export interface TranscriptionSegment {
  start_time: number;
  end_time: number;
  speaker: 'care_coordinator' | 'member' | 'unknown';
  text: string;
  confidence?: number;
}

export interface TranscriptionResult {
  id: number;
  telehealth_session_id: number;
  status: TranscriptionStatus;
  transcript_text?: string;
  segments: TranscriptionSegment[];
  duration_seconds?: number;
  started_at?: string;
  completed_at?: string;
  ai_summary?: string;
  ai_suggested_notes?: {
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
  };
  ai_extracted_sdoh_domains?: string[];
}

export interface TelehealthEncounterConfig {
  id: number;
  cbo_id: number;
  program_id?: number;
  encounter_form_template_id?: number;
  voice_ai_enabled: boolean;
  voice_ai_auto_prompt_consent: boolean;
  voice_ai_consent_form_template_id?: number;
  voice_ai_retain_audio_days: number;
  voice_ai_auto_populate_notes: boolean;
  voice_ai_provider: VoiceAIProvider;
  default_sdoh_domains?: string[];
  auto_create_case_note: boolean;
  auto_link_to_activity: boolean;
  require_sign_before_close: boolean;
  consent_script_text?: string;
  created_at: string;
  updated_at: string;
}

export interface EncounterConfigUpdate {
  encounter_form_template_id?: number;
  voice_ai_enabled?: boolean;
  voice_ai_auto_prompt_consent?: boolean;
  voice_ai_consent_form_template_id?: number;
  voice_ai_retain_audio_days?: number;
  voice_ai_auto_populate_notes?: boolean;
  voice_ai_provider?: VoiceAIProvider;
  default_sdoh_domains?: string[];
  auto_create_case_note?: boolean;
  auto_link_to_activity?: boolean;
  require_sign_before_close?: boolean;
  consent_script_text?: string;
}

export interface SDOHDomainDefinition {
  id: number;
  domain_key: string;
  display_name: string;
  description?: string;
  icon_name?: string;
  color?: string;
  sort_order: number;
  is_active: boolean;
}

// Extended session type with voice AI fields
export interface TelehealthSessionWithVoiceAI extends TelehealthSession {
  voice_ai_consent_status: VoiceAIConsentStatus;
  voice_ai_consent_timestamp?: string;
  voice_ai_consented_by_member_id?: number;
  voice_ai_enabled?: boolean;
  encounter_form_template_id?: number;
  voice_ai_auto_prompt_consent?: boolean;
  consent_script_text?: string;
  transcription_id?: number;
  transcription_status?: TranscriptionStatus;
}

// Extended encounter type with form fields
export interface TelehealthEncounterWithForm extends TelehealthEncounter {
  form_template_id?: number;
  form_instance_id?: number;
}

// ============================================================================
// VOICE AI COMPONENT PROPS
// ============================================================================

export interface VoiceAIConsentModalProps {
  open: boolean;
  session: TelehealthSession;
  consentScript?: string;
  onConsent: (granted: boolean, method: VoiceAIConsentMethod, notes?: string) => void;
  onClose: () => void;
}

export interface VoiceAITranscriptionPanelProps {
  sessionId: number;
  isActive: boolean;
  onApplySuggestions?: (fields: string[]) => void;
  className?: string;
}

export interface EncounterFormRendererProps {
  encounterId: number;
  formTemplateId?: number;
  memberId: number;
  isEditing: boolean;
  onUpdate: (fieldData: Record<string, unknown>) => void;
  onSave: () => void;
  aiSuggestions?: TranscriptionResult['ai_suggested_notes'];
  className?: string;
}

export interface EncounterConfigSettingsProps {
  config?: TelehealthEncounterConfig;
  onSave: (updates: EncounterConfigUpdate) => void;
  className?: string;
}

// ============================================================================
// VOICE AI CONSTANTS
// ============================================================================

export const VOICE_AI_CONSENT_STATUS_LABELS: Record<VoiceAIConsentStatus, string> = {
  not_requested: 'Not Requested',
  pending: 'Pending',
  granted: 'Granted',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
};

export const VOICE_AI_CONSENT_STATUS_COLORS: Record<VoiceAIConsentStatus, string> = {
  not_requested: 'gray',
  pending: 'yellow',
  granted: 'green',
  denied: 'red',
  withdrawn: 'orange',
};

export const TRANSCRIPTION_STATUS_LABELS: Record<TranscriptionStatus, string> = {
  recording: 'Recording',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const DEFAULT_CONSENT_SCRIPT = `Before we begin our session, I want to let you know that we have the option to use AI-powered transcription to help document our conversation. This technology will:

• Create a real-time transcript of our conversation
• Help me take more accurate notes about what we discuss
• Allow me to focus on you rather than typing

The recording and transcript are:
• Confidential and protected by healthcare privacy laws
• Only used for your care documentation
• Not shared with anyone outside your care team

You can choose to decline this, and it won't affect your care in any way. You can also ask me to stop the recording at any time during our call.

Do you consent to having our conversation transcribed today?`;
